import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { FiCheck, FiUsers, FiWifi, FiArrowRight, FiMapPin, FiDroplet, FiAward, FiCoffee, FiCalendar, FiFileText } from 'react-icons/fi';
import OtherOccasions from '../components/OtherOccasions';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import QuoteModal from '../components/quote/QuoteModal';
import { corporateFAQs } from '../data/faqs';
import { useSettings } from '../context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Rotating hero images for the Corporate page. First is the existing banner
// (corporate dinner setup under yellow umbrellas), then two new indoor
// strategy/brainstorming room shots. Auto-cycles every 5s with a soft fade.
const HERO_IMAGES = [
  'https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/z4eom8ql_Corporate%20event.png',
  'https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/587e6b8bf197260f_Gemini_Generated_Image_xkg996xkg996xkg9.png',
  'https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/b2b23d6c44fae0de_Gemini_Generated_Image_6opmb96opmb96opm.png',
];

const Corporate = () => {
  const { nightDiscountPct, nightDiscountEnabled } = useSettings();
  const navigate = useNavigate();
  const [heroIdx, setHeroIdx] = useState(0);
  const [heroPaused, setHeroPaused] = useState(false);
  const [showQuote, setShowQuote] = useState(false);
  useEffect(() => {
    if (heroPaused) return undefined;
    const t = setInterval(() => setHeroIdx((i) => (i + 1) % HERO_IMAGES.length), 5000);
    return () => clearInterval(t);
  }, [heroPaused]);
  // Live-fetch the 12BHK Celebration Villa rate so marketing copy stays in sync
  // with the admin-editable rate card (no hardcoded prices in long-form content).
  const [villa12bhk, setVilla12bhk] = useState(null);
  useEffect(() => {
    axios.get(`${API}/villas/12bhk-celebration-villa-lonavala`)
      .then(r => setVilla12bhk(r.data))
      .catch(() => { /* non-critical — fallback to '—' placeholders */ });
  }, []);
  const inr = (n) => (typeof n === 'number' ? `Rs.${n.toLocaleString('en-IN')}` : '—');

  // Direct-book inputs — minimum nights = 1, auto check-out = check-in + 1
  const tomorrow = new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const dayAfter = new Date(Date.now() + 2 * 86400000).toISOString().split('T')[0];
  const [bookCheckIn, setBookCheckIn] = useState(tomorrow);
  const [bookCheckOut, setBookCheckOut] = useState(dayAfter);
  const [bookGuests, setBookGuests] = useState(24);
  const [bookErr, setBookErr] = useState('');

  const handleBook12bhk = () => {
    setBookErr('');
    if (!bookCheckIn || !bookCheckOut) { setBookErr('Please pick both dates.'); return; }
    if (new Date(bookCheckOut) <= new Date(bookCheckIn)) { setBookErr('Check-out must be after check-in.'); return; }
    if (bookGuests < 1 || bookGuests > 35) { setBookErr('Guests must be between 1 and 35.'); return; }
    const params = new URLSearchParams({
      villa: '12bhk-celebration-villa-lonavala',
      checkin: bookCheckIn,
      checkout: bookCheckOut,
      adults: String(bookGuests),
    });
    navigate(`/booking?${params.toString()}`);
  };

  // Speakable schema — tells voice assistants (Google Assistant, Alexa) and AI
  // answer engines (Perplexity, Google SGE, ChatGPT Search, Gemini) which
  // concise blocks to read aloud / quote verbatim when answering corporate
  // offsite / cooking space / Wi-Fi / 35-person queries.
  useEffect(() => {
    const speakableSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Corporate Offsite Villa Lonavala on Rent for up to 35 Persons',
      description: 'Villa for corporate offsite trip for upto 35 persons with private swimming pool Lonavala on rent — 12BHK Celebration Villa with complimentary Wi-Fi and Dedicated Cooking Space for your own chef / maharaj.',
      speakable: {
        '@type': 'SpeakableSpecification',
        cssSelector: ['[data-speakable]'],
      },
      url: 'https://royalgardenvillas.in/corporate',
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-testid', 'corporate-speakable-schema');
    el.text = JSON.stringify(speakableSchema).replace(/</g, '\\u003c');
    document.head.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);

  return (
    <div data-testid="corporate-page" className="pt-20">
      <SEO
        title="Villa for 30–35 People for Corporate Offsite Trip in Lonavala — Private Pool, Wi-Fi | Royal Garden Villas"
        description="Villa for corporate offsite trip for upto 35 persons with private swimming pool Lonavala on rent — 12BHK Celebration Villa (up to 35 guests). Complimentary 50 Mbps Microscan fiber Wi-Fi, inverter backup, large TV + whiteboard, 3 private pools, 3 lawns, plus a Dedicated Cooking Space to bring your own chef / maharaj. 90 min from Mumbai, 70 min from Pune."
        path="/corporate"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Corporate Retreats', url: '/corporate' },
        ]}
      />
      {/* Hero — crossfading slideshow of 3 corporate shots. Auto-advances
          every 5s; pauses while visitor hovers (desktop) or long-presses
          (mobile via touchstart/touchend) so overlay copy stays readable. */}
      <section
        className="relative h-[50vh] min-h-[360px] overflow-hidden"
        data-testid="corporate-hero"
        onMouseEnter={() => setHeroPaused(true)}
        onMouseLeave={() => setHeroPaused(false)}
        onTouchStart={() => setHeroPaused(true)}
        onTouchEnd={() => setHeroPaused(false)}
      >
        {HERO_IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={i === 0 ? 'Corporate event hosting at Royal Garden Villas' : i === 1 ? 'Corporate strategy session inside a Royal Garden Villa' : 'Executive brainstorming meeting inside a Royal Garden Villa'}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${i === heroIdx ? 'opacity-100' : 'opacity-0'}`}
            width="1920" height="1080"
            loading={i === 0 ? 'eager' : 'lazy'}
            fetchpriority={i === 0 ? 'high' : 'low'}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E]/80 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-7xl mx-auto">
          <p className="text-[#F57C20] text-xs font-semibold tracking-[0.2em] uppercase mb-3">Corporate Offsites · Up to 35 Persons</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Corporate Offsite Villa in <span className="text-[#F57C20]">Lonavala on Rent</span>
          </h1>
          <p className="text-white/80 font-light mt-3 max-w-xl text-sm">
            A private-pool villa compound for your corporate offsite trip for up to 35 persons &mdash; 12 bedrooms, 3 private swimming pools, complimentary 50 Mbps fiber Wi-Fi, inverter backup, and a Dedicated Cooking Space for your own chef / maharaj. 90 minutes from Mumbai.
          </p>
          <button
            type="button"
            data-testid="corp-hero-get-quote-btn"
            onClick={() => setShowQuote(true)}
            className="mt-5 inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#E06A10] text-white px-6 py-3 rounded-lg text-sm font-semibold tracking-wide transition-colors shadow-lg shadow-orange-500/30"
          >
            <FiFileText className="w-4 h-4" /> Get Quote for Corporate Offsite
          </button>
        </div>
        {/* Slide indicators */}
        <div className="absolute bottom-3 right-6 flex gap-1.5 z-10" data-testid="hero-slide-dots">
          {HERO_IMAGES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setHeroIdx(i)}
              aria-label={`Show slide ${i + 1}`}
              data-testid={`hero-slide-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${i === heroIdx ? 'w-6 bg-[#F57C20]' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
            />
          ))}
        </div>
      </section>

      {/* ===== SEO-RICH KEYWORD INTRO (above-the-fold banner for Google + AI search) ===== */}
      <section className="bg-white border-y border-[#F57C20]/15">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
            <div className="lg:col-span-2">
              <p className="text-[#F57C20] text-[11px] font-semibold tracking-[0.2em] uppercase mb-2">Corporate Offsite Trip &middot; Lonavala</p>
              <h2 data-speakable="true" className="text-2xl md:text-3xl text-[#1A1A2E] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                Villa for Corporate Offsite Trip for Upto 35 Persons with Private Swimming Pool Lonavala on Rent
              </h2>
              <div data-speakable="true">
                <p className="text-[#374151] text-sm md:text-[15px] leading-relaxed mb-3">
                  Royal Garden Villas is the preferred <strong>private pool villa compound in Lonavala on rent for corporate offsites of up to 35 persons</strong>. Our flagship 12BHK Celebration Villa combines three independent villas (3BHK + 4BHK + 5BHK) inside one gated compound — hosting <strong>24 to 35 team members</strong> comfortably across 12 bedrooms, 3 private pools, 3 lawns and 3 kitchens.
                </p>
                <p className="text-[#374151] text-sm md:text-[15px] leading-relaxed">
                  Three private swimming pools, three private lawns, <strong>complimentary 50 Mbps Microscan fiber Wi-Fi</strong> across every villa, inverter power backup on lights &amp; Wi-Fi, large TV with HDMI casting + whiteboard for presentations, and a <span className="text-[#F57C20] font-semibold">NEW · Dedicated Cooking Space</span> for teams who want to bring their own chef or <em>maharaj</em> and cook authentic Indian meals during the offsite. 90 minutes from Mumbai, 70 minutes from Pune.
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] rounded-2xl p-6 text-white">
              <FiCoffee className="w-8 h-8 mb-3" />
              <p className="text-[11px] font-bold tracking-[0.2em] uppercase mb-2 opacity-90">New &middot; Optional</p>
              <h3 className="text-xl mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Bring Your Own Chef or Maharaj</h3>
              <p className="text-white/85 text-xs leading-relaxed mb-3">
                A unique Dedicated Cooking Space with gas stove, utensils, steel platform and washing sink — exclusively for your team&apos;s chef / <em>maharaj</em> to cook home-style Jain, Pure-Veg, non-veg or regional meals outdoors. Not available at competitor villas or Airbnb listings in Lonavala.
              </p>
              <p className="text-white/70 text-[11px]">
                Rs.7,500/day (12BHK) &middot; Rs.4,500/day (7/8/9 BHK) &middot; Rs.3,000/day (3/4/5 BHK)
              </p>
            </div>
          </div>

          {/* ===== CORPORATE QUICK FACTS (voice-friendly, quotable by AI engines) ===== */}
          <div data-testid="corporate-quick-facts" data-speakable="true" className="mt-8 bg-gradient-to-br from-[#F7F8FA] to-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-[#1A1A2E] text-sm font-bold tracking-wider uppercase mb-3" style={{ letterSpacing: '0.1em' }}>Corporate Offsite — Quick Facts</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-[#374151] text-sm">
              <li><strong>Max team size:</strong> 24-35 team members across 12 bedrooms</li>
              <li><strong>Private villas:</strong> 3 villas, 12 bedrooms, 3 pools</li>
              <li><strong>Wi-Fi:</strong> Complimentary 50 Mbps Microscan fiber (every villa)</li>
              <li><strong>Power backup:</strong> Inverter on lights, Wi-Fi, TVs &amp; charging points</li>
              <li><strong>Meeting setup:</strong> Large TV + HDMI casting + whiteboard (included)</li>
              <li><strong>Dedicated Cooking Space:</strong> Optional — bring your own chef / maharaj</li>
              <li><strong>Distance:</strong> 90 min from Mumbai · 70 min from Pune</li>
              <li><strong>Weekday rate (12BHK):</strong> From {inr(villa12bhk?.base_price)} per night</li>
              <li><strong>Weekend rate (12BHK):</strong> From {inr(villa12bhk?.weekend_price)} per night</li>
              {nightDiscountEnabled && <li><strong>Discount:</strong> {nightDiscountPct}% off from 2nd night (Stay Longer, Pay Less)</li>}
              <li><strong>Billing:</strong> Detailed invoice for corporate T&amp;E</li>
              <li><strong>Contact:</strong> WhatsApp +91 98200 88292</li>
            </ul>
          </div>

          {/* ===== DIRECT-BOOK 12BHK WIDGET (lets corporate planners instantly check availability + book without a form round-trip) ===== */}
          <div data-testid="corporate-direct-book-card" className="mt-8 relative overflow-hidden bg-gradient-to-br from-[#1A1A2E] via-[#1B2432] to-[#F57C20]/90 rounded-2xl p-6 md:p-8 shadow-2xl shadow-[#1A1A2E]/30">
            {/* subtle grain pattern */}
            <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: '18px 18px' }} />
            <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
              <div className="lg:col-span-5">
                <p className="text-[#F57C20] text-[11px] font-bold tracking-[0.25em] uppercase mb-2">Direct Booking &middot; 12BHK Celebration Villa</p>
                <h3 className="text-2xl md:text-3xl text-white mb-2" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
                  Book Your Corporate Offsite in <span className="text-[#F57C20]">60 Seconds</span>
                </h3>
                <p className="text-white/80 text-sm leading-relaxed mb-3">
                  Instant online booking with Razorpay &mdash; no form round-trips, no email chasing. Confirmation lands in your inbox before the meeting ends.
                </p>
                <div className="flex items-baseline gap-2 text-white">
                  <span className="text-xs uppercase tracking-wider opacity-70">From</span>
                  <span className="text-3xl font-bold">{inr(villa12bhk?.base_price)}</span>
                  <span className="text-xs opacity-70">/ night (weekday)</span>
                </div>
                <p className="text-white/60 text-[11px] mt-1">Weekend from {inr(villa12bhk?.weekend_price)}{nightDiscountEnabled ? ` · ${nightDiscountPct}% off 2nd night onwards` : ''}</p>
              </div>

              <div className="lg:col-span-7 bg-white/95 backdrop-blur rounded-xl p-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                  <div>
                    <label className="block text-[#6B7280] text-[10px] font-bold uppercase tracking-wider mb-1.5">Check-in</label>
                    <input
                      type="date"
                      data-testid="corp-book-checkin"
                      value={bookCheckIn}
                      min={tomorrow}
                      onChange={(e) => {
                        setBookCheckIn(e.target.value);
                        if (bookCheckOut && new Date(bookCheckOut) <= new Date(e.target.value)) {
                          const next = new Date(e.target.value); next.setDate(next.getDate() + 1);
                          setBookCheckOut(next.toISOString().split('T')[0]);
                        }
                      }}
                      className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm font-medium focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#6B7280] text-[10px] font-bold uppercase tracking-wider mb-1.5">Check-out</label>
                    <input
                      type="date"
                      data-testid="corp-book-checkout"
                      value={bookCheckOut}
                      min={bookCheckIn || tomorrow}
                      onChange={(e) => setBookCheckOut(e.target.value)}
                      className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm font-medium focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#6B7280] text-[10px] font-bold uppercase tracking-wider mb-1.5">Team size</label>
                    <input
                      type="number"
                      data-testid="corp-book-guests"
                      value={bookGuests}
                      min={1}
                      max={35}
                      onChange={(e) => setBookGuests(parseInt(e.target.value, 10) || 1)}
                      className="w-full bg-[#F7F8FA] border border-gray-200 rounded-lg px-3 py-2.5 text-[#1A1A2E] text-sm font-medium focus:outline-none focus:border-[#F57C20] focus:ring-1 focus:ring-[#F57C20]"
                    />
                  </div>
                </div>
                {bookErr && <p data-testid="corp-book-err" className="text-red-600 text-xs mb-2">{bookErr}</p>}
                <button
                  type="button"
                  data-testid="corp-book-submit"
                  onClick={handleBook12bhk}
                  className="w-full bg-[#F57C20] hover:bg-[#E06A10] text-white px-6 py-3 rounded-lg text-sm font-semibold tracking-wide transition-all flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-orange-500/40"
                >
                  <FiCalendar className="w-4 h-4" />
                  Check Availability &amp; Book 12BHK
                  <FiArrowRight className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  data-testid="corp-get-quote-btn"
                  onClick={() => setShowQuote(true)}
                  className="mt-2 w-full border-2 border-[#1A1A2E] text-[#1A1A2E] hover:bg-[#1A1A2E] hover:text-white px-6 py-2.5 rounded-lg text-sm font-semibold tracking-wide transition-colors flex items-center justify-center gap-2"
                >
                  <FiFileText className="w-4 h-4" />
                  Get Quote for Corporate Offsite (PDF)
                </button>
                <p className="text-[#6B7280] text-[11px] mt-2 text-center">
                  Need more than 35 persons? WhatsApp us on&nbsp;
                  <a href="https://wa.me/919820088292" className="text-[#F57C20] font-semibold hover:underline" target="_blank" rel="noreferrer">+91 98200 88292</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>
            Why Teams <span className="text-[#F57C20]">Choose Us</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: FiUsers, title: 'Up to 35 Team Members', desc: 'Flagship 12BHK comfortably hosts 24-35 team members across 12 bedrooms and 3 private pools for your corporate offsite.' },
              { icon: FiWifi, title: 'Complimentary 50 Mbps Fiber Wi-Fi', desc: 'Complimentary Microscan fiber Wi-Fi across every villa — reliable for team video calls (Zoom, MS Teams) and workations.' },
              { icon: FiDroplet, title: '3 Private Swimming Pools', desc: 'Private swimming pools exclusively for your team — pool-side team-building and evening celebrations.' },
              { icon: FiAward, title: 'Dedicated Cooking Space', desc: 'NEW · Bring your own chef / maharaj and cook authentic Indian meals outdoors. Unique add-on.' },
            ].map((item, i) => (
              <div key={i} className="bg-white p-6 rounded-xl border border-gray-100">
                <item.icon className="w-6 h-6 text-[#F57C20] mb-4" />
                <h3 className="text-[#1A1A2E] text-sm font-semibold mb-2">{item.title}</h3>
                <p className="text-[#6B7280] text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Venue Gallery */}
      <section className="py-16 md:py-20 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-white mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Our <span className="text-[#F57C20]">Venues</span></h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              // Row 1 — NEW coral/yellow-sofas corporate shot leads, flanked by signature villa property shots.
              'https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/fjlbdzk6_15.jpg',
              'https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/587e6b8bf197260f_Gemini_Generated_Image_xkg996xkg996xkg9.png',
              '/images/5bhk/2.jpg',
              '/images/12bhk/1.jpg',
              // Row 2 — NEW purple-wall strategy session leads, followed by two new outdoor corporate meeting shots (daytime & evening).
              'https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/b2b23d6c44fae0de_Gemini_Generated_Image_6opmb96opmb96opm.png',
              '/images/3bhk/3.jpg',
              'https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/23bdce699579ffe3_image.png',
              'https://customer-assets-wrfwihn1.emergentagent.net/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/e1f39e5c1a21a1be_image.png',
            ].map((img, i) => (
              <div key={i} className="aspect-[4/3] rounded-lg overflow-hidden" data-testid={`venue-tile-${i}`}>
                <img src={img} alt={`Corporate venue ${i + 1} at Royal Garden Villas Lonavala`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-16 md:py-20 bg-[#F7F8FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-light text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>What's <span className="text-[#F57C20]">Included</span></h2>
              <ul className="space-y-3">
                {[
                  "Seating capacity for 35+ people across common areas",
                  "Multiple breakout rooms for team discussions",
                  "Complimentary 50 Mbps Microscan fiber Wi-Fi across every villa",
                  "Large TV (HDMI casting) + whiteboard for presentations",
                  "Inverter power backup on lights, complimentary Wi-Fi, TVs & charging points",
                  "Outdoor lawn areas for team activities and games",
                  "3 private pools for recreation",
                  "BBQ area for team dinners and bonfire nights",
                  "Ample parking for 6 cars (12BHK compound)",
                  "Dedicated property manager for your event",
                  "Near Lonavala's top attractions for team outings"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <FiCheck className="w-4 h-4 text-[#F57C20] mt-0.5 flex-shrink-0" />
                    <span className="text-[#6B7280] text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="text-2xl font-light text-[#1A1A2E] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Popular <span className="text-[#F57C20]">Add-ons</span></h2>
              <div className="space-y-4">
                {/* Maid Service */}
                <div className="bg-white border border-gray-100 rounded-xl p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-[#1A1A2E] text-sm font-semibold block">Extra Butler Service (Optional)</span>
                      <span className="text-[#9CA3AF] text-xs">Dedicated maid for meal serving (1 PM - 9 PM)</span>
                    </div>
                    <span className="text-[#F57C20] font-bold text-sm whitespace-nowrap ml-4">Rs.2,000/day</span>
                  </div>
                  <p className="text-[#6B7280] text-[11px] mt-1">Serving lunch, high tea & dinner. Charges for serving only, not cooking/supply.</p>
                </div>

                {/* BBQ Service */}
                <div className="bg-white border-2 border-[#F57C20]/20 rounded-xl p-5">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-[#1A1A2E] text-sm font-semibold block">BBQ Setup (Optional)</span>
                      <span className="text-[#9CA3AF] text-xs">Sigdi, Metal Sticks, Charcoal & Labour per meal (2 hrs)</span>
                    </div>
                    <span className="text-[#F57C20] font-bold text-sm whitespace-nowrap ml-4">Rs.3,000/meal</span>
                  </div>
                  <div className="bg-[#F7F8FA] rounded-lg p-3 mt-2 space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Base (1 Labour included)</span>
                      <span className="text-[#1A1A2E] font-semibold">Rs.3,000/meal</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-[#6B7280]">Additional Labour</span>
                      <span className="text-[#1A1A2E] font-semibold">+Rs.1,000/person/meal</span>
                    </div>
                  </div>
                  <p className="text-[#F57C20]/70 text-[10px] mt-2 italic">Max 2 meals/day. Food not included. Care to be taken of property.</p>
                </div>

                {/* Bonfire */}
                <div className="bg-white border border-gray-100 rounded-xl p-4 flex justify-between items-center">
                  <div>
                    <span className="text-[#1A1A2E] text-sm font-medium block">Bonfire Setup (Optional)</span>
                    <span className="text-[#9CA3AF] text-xs">Sigdi and wooden sticks for a cozy bonfire</span>
                  </div>
                  <span className="text-[#F57C20] font-bold text-xs whitespace-nowrap ml-4">Rs.2,000/session</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flagship Villa */}
      <section className="py-16 md:py-20 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_ec907cab-2737-4c0e-a014-2b1aed568ccf/artifacts/8uotvybj_2.jpg"
                alt="12BHK Celebration Villa"
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <FiMapPin className="w-5 h-5 text-[#F57C20] mb-4" />
              <h3 className="text-2xl font-light text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                12BHK <span className="text-[#F57C20]">Celebration Villa</span>
              </h3>
              <p className="text-white/50 text-sm font-light mb-6 leading-relaxed">
                Our flagship property combines all 3 villas into one grand venue with 12 bedrooms, 3 pools, expansive lawns, and common areas — perfect for large corporate teams of up to 35 guests.
              </p>
              <Link to="/villas/12bhk-celebration-villa-lonavala" className="inline-flex items-center gap-2 bg-[#F57C20] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#E06A10] transition-all">
                View Villa Details <FiArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Long-form SEO content — corporate retreat guide */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-light text-[#1A1A2E] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            The Complete Guide to Hosting a Corporate Retreat in Lonavala &mdash; Villa for Up to 35 Persons
          </h2>
          <div className="space-y-4 text-[#374151] text-sm md:text-base leading-relaxed">
            <p>
              For companies based in Mumbai, Pune or Bangalore, Lonavala has become the go-to destination for offsite retreats &mdash; close enough for a quick weekend, far enough to actually disconnect, and scenic enough to make the trip feel like a perk rather than a project. Royal Garden Villas is one of the most preferred <strong>luxury villas for a corporate offsite trip for up to 35 persons with private swimming pool in Lonavala on rent</strong>, hosting leadership offsites, team-building weekends, sales kickoffs and quarterly all-hands events across our 12BHK Celebration Villa (with a nearby overflow villa coordinated when teams exceed 35).
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Why Companies Choose Royal Garden Villas for Offsites</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Exclusive use of the private compound</strong> &mdash; the 12BHK Celebration Villa reserves all three villas inside the gated estate for your group only. No other guests, no shared meeting spaces, full privacy for strategy discussions.</li>
              <li><strong>Capacity for up to 35 team members</strong> &mdash; the 12BHK Celebration Villa comfortably hosts 24-35 guests across 12 bedrooms, 3 private pools and 3 lawns inside one gated compound.</li>
              <li><strong>Multiple breakout zones &amp; private pools</strong> &mdash; three connected villas with three private swimming pools and three lawns give natural breakouts. Run a plenary session in one villa, parallel workshops in the other two, and coffee breaks by the pool.</li>
              <li><strong>Complimentary 50 Mbps Microscan fiber Wi-Fi</strong> &mdash; complimentary reliable internet across all villas, suitable for hybrid sessions, Zoom / Google Meet / MS Teams presentations, and remote team members joining virtually.</li>
              <li><strong>Inverter power backup</strong> &mdash; on lights, complimentary Wi-Fi router, TVs and charging points, so short local outages do not disrupt sessions or video calls (AC runs on grid power).</li>
              <li><strong>Large TV + Whiteboard on-site</strong> &mdash; HDMI casting for slide decks, whiteboard for brainstorms and workshops (no separate rental needed).</li>
              <li><strong>NEW &middot; Dedicated Cooking Space</strong> &mdash; the only private pool villa in Lonavala on rent for corporate offsites that lets your team <strong>bring your own chef / maharaj</strong> and cook authentic Jain, Pure-Veg, non-veg, Continental or regional meals outdoors. Gas, utensils, steel platform and washing sink included; competitor villas and Airbnb listings do not offer this. Charged at Rs.7,500/day (12BHK), Rs.4,500/day (7/8/9 BHK) or Rs.3,000/day (3/4/5 BHK).</li>
              <li><strong>90 minutes from Mumbai, 70 minutes from Pune</strong> &mdash; travel time short enough for Friday-evening arrivals after the workday.</li>
              <li><strong>Catering flexibility</strong> &mdash; bring your own caterer, hire ours, order from Swiggy / Zomato / local cloud kitchens, or use the Dedicated Cooking Space with your own maharaj. We do not enforce any catering monopoly.</li>
              <li><strong>Corporate billing</strong> &mdash; detailed booking invoice for corporate reimbursement; advance and balance payments accepted via Razorpay.</li>
            </ul>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Which Villa Combination Fits Your Team Size?</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>8-10 team members:</strong> 4BHK Reunion Villa &mdash; one private pool, one villa for 8 person / villa for 10 person retreats.</li>
              <li><strong>10-15 team members:</strong> 5BHK Boho Villa &mdash; one private pool, largest single villa, villa for 10 person team offsites with room to breathe.</li>
              <li><strong>14-18 team members:</strong> 7BHK Harmony Villa combo (3+4 BHK) &mdash; two private pools, two kitchens, villa for 14 person / villa for 16 person / villa for 18 persons stays.</li>
              <li><strong>16-20 team members:</strong> 8BHK Grandeur Villa combo (3+5 BHK) &mdash; two private pools, baby pool, hot tub, BBQ area.</li>
              <li><strong>18-22 team members:</strong> 9BHK Paradise Villa combo (4+5 BHK) &mdash; two private pools, group swing, hot tub, mountain views.</li>
              <li><strong>24-35 team members:</strong> 12BHK Celebration Villa &mdash; the full private compound with 3 private pools.</li>
              <li><strong>24-35 team members:</strong> 12BHK Celebration Villa — the flagship combo compound for a corporate offsite trip for upto 35 persons.</li>
            </ul>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">A Typical Corporate Retreat Schedule</h3>
            <p>
              <strong>Friday evening:</strong> Team arrival 6&ndash;8 PM. Welcome drinks on the lawn, ice-breakers, BBQ dinner (or home-style meal cooked by your own <em>maharaj</em> in the Dedicated Cooking Space), bonfire with informal team conversations.
            </p>
            <p>
              <strong>Saturday:</strong> Morning workshop or strategy session in the main living room (capacity 30+). Lunch served villa-side. Afternoon: outdoor team activities &mdash; pool games, lawn games, or a guided trek to nearby Lion&apos;s Point. Evening: themed dinner, awards/recognition, music.
            </p>
            <p>
              <strong>Sunday:</strong> Closing session, vision-setting for the quarter, team photographs on the lawn, leisurely brunch, departure by 11 AM.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Event Setup You Can Arrange</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Large TV + Whiteboard for presentations &mdash; HDMI casting, no rental needed</li>
              <li>Flip charts and stationery on request</li>
              <li>Theme decor for product launches and milestone celebrations</li>
              <li>Dedicated Cooking Space with your own chef or <em>maharaj</em> for authentic Indian meals</li>
              <li>Photographer / videographer referrals for retreat documentation</li>
              <li>Yoga instructor / wellness session add-ons for wellbeing-focused offsites</li>
            </ul>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Pricing &amp; Booking</h3>
            <p>
              The 12BHK Celebration Villa is our flagship retreat venue, with all-inclusive nightly rates from {inr(villa12bhk?.base_price)} (weekday) to {inr(villa12bhk?.weekend_price)} (weekend). For mid-size teams (10&ndash;22 people), the 7BHK, 8BHK or 9BHK combo villas are excellent options at significantly lower budgets.{nightDiscountEnabled && <> The <strong>&ldquo;Stay Longer, Pay Less&rdquo;</strong> {nightDiscountPct}% discount applies from the 2nd night onwards on all corporate bookings &mdash;</>} please request a written quote through the contact form or call directly.
            </p>
            <h3 className="text-lg font-semibold text-[#1A1A2E] mt-6">Frequently Asked: Corporate Bookings</h3>
            <p>
              <strong>Q: Can we host a corporate offsite trip for upto 35 persons at your Lonavala villa?</strong><br/>
              A: Yes &mdash; the 12BHK Celebration Villa comfortably hosts 24-35 team members for your corporate offsite. Reach out on WhatsApp +91 98200 88292 with your dates &amp; team size.
            </p>
            <p>
              <strong>Q: Can our chef or maharaj cook meals during the offsite?</strong><br/>
              A: Yes. Our optional Dedicated Cooking Space (Rs.7,500/day for the 12BHK) gives your chef an outdoor cooking area with gas, utensils, steel platform &amp; washing sink. Gas cooking is NOT permitted inside the villa; only induction + microwave inside.
            </p>
            <p>
              <strong>Q: Can we run a paid client event or seminar at the property?</strong><br/>
              A: Internal team events, offsites and small client meetings &mdash; yes, absolutely. Ticketed public events or weddings with 100+ attendees &mdash; please discuss with us first; we have terms specific to large events.
            </p>
            <p>
              <strong>Q: Do you provide a detailed invoice for corporate reimbursement?</strong><br/>
              A: Yes. We email a detailed booking invoice (property, dates, guests, nightly rate, tax break-up, payment mode) &mdash; suitable for corporate T&amp;E claims. GST invoicing is not currently issued.
            </p>
            <p>
              <strong>Q: How early should we book?</strong><br/>
              A: 6&ndash;8 weeks in advance for weekends and 10+ weeks for long weekends. Peak season (Dec&ndash;Jan) books out fastest.
            </p>
          </div>
        </div>
      </section>

      {/* Internal linking — other occasions */}
      <OtherOccasions currentPath="/corporate" heading="Also Perfect For" />

      {/* CTA */}
      <section className="py-16 border-t border-white/5 bg-[#1A1A2E]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h3 className="text-xl font-light text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Plan Your Corporate Retreat</h3>
          <p className="text-white/40 font-light mb-8 text-sm">Get a custom quote for your team size and requirements</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+919820088292" className="bg-[#F57C20] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#E06A10] transition-all">
              Call +91 98200 88292
            </a>
            <Link to="/contact" className="border-2 border-white/20 text-white px-8 py-3 rounded-lg text-sm font-semibold hover:border-[#F57C20] hover:text-[#F57C20] transition-all flex items-center justify-center gap-2">
              Request Quote <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* ===================== CORPORATE FAQ (with JSON-LD schema for AI-search) ===================== */}
      <FAQSection
        title="Corporate Offsite FAQ"
        subtitle="Everything decision-makers need to know before booking Royal Garden Villas for a team offsite or retreat."
        testid="corporate-faq"
        items={corporateFAQs}
      />
      <QuoteModal
        open={showQuote}
        onClose={() => setShowQuote(false)}
        mode="guest"
        fixedVillaId="12bhk-celebration-villa-lonavala"
        initial={{ check_in: bookCheckIn, check_out: bookCheckOut, guests: bookGuests }}
        title="Corporate Offsite Quote — 12BHK Celebration Villa"
      />
    </div>
  );
};

export default Corporate;
